import React from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { Patient, CreatePatientDto, UpdatePatientDto } from '../../types/patient.types';
import { Input } from '../common/Input';
import { Select } from '../common/Select';
import { Button } from '../common/Button';
import { User, Calendar, Phone, Users } from 'lucide-react';

interface PatientFormProps {
  patient?: Patient;
  onSubmit: (data: CreatePatientDto | UpdatePatientDto) => void;
  isLoading?: boolean;
}

export const PatientForm: React.FC<PatientFormProps> = ({
  patient,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<CreatePatientDto>({
    defaultValues: patient
      ? {
          name: patient.name,
          age: patient.age,
          phone: patient.phone,
          gender: patient.gender,
        }
      : undefined,
  });

  const genderOptions = [
    { value: 'Male', label: t('patients.male', 'Male') },
    { value: 'Female', label: t('patients.female', 'Female') },

  ];

  const handleFormSubmit = (data: CreatePatientDto | UpdatePatientDto) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="position-relative" style={{ zIndex: 2 }}>
      {/* Patient Information Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-4"
      >
        <motion.h3
          className="mb-4 fw-bold"
          style={{
            fontSize: '1.125rem',
            color: '#1f2937',
            letterSpacing: '0.01em',
          }}
        >
          {t('patients.patientDetails', 'Patient Information')}
        </motion.h3>

        <div className="space-y-4">
          {/* Name Field */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <Input
              label={t('patients.patientName', 'Patient Name')}
              required
              error={errors.name?.message ? t('errors.required', 'This field is required') : undefined}
              icon={<User className="h-5 w-5" strokeWidth={2} />}
              {...register('name', { required: t('errors.required', 'This field is required') })}
            />
          </motion.div>

          {/* Age Field */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <Input
              label={t('patients.age', 'Age')}
              type="number"
              required
              error={errors.age?.message}
              icon={<Calendar className="h-5 w-5" strokeWidth={2} />}
              min={1}
              max={99}
              {...register('age', {
                required: t('errors.required', 'This field is required'),
                min: { value: 1, message: t('patients.ageMinError', 'Age must be greater than 0') },
                max: { value: 99, message: t('patients.ageMaxError', 'Age must be 99 or less (2 digits maximum)') },
                valueAsNumber: true,
              })}
            />
          </motion.div>

          {/* Phone Field */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <Input
              label={t('patients.phone', 'Phone')}
              required
              error={errors.phone?.message}
              icon={<Phone className="h-5 w-5" strokeWidth={2} />}
              maxLength={11}
              {...register('phone', {
                required: t('errors.required', 'This field is required'),
                maxLength: {
                  value: 11,
                  message: t('patients.phoneMaxLength', 'Phone number must not exceed 11 digits')
                },
                pattern: {
                  value: /^[0-9]+$/,
                  message: t('patients.phoneInvalid', 'Phone number must contain only digits')
                }
              })}
            />
          </motion.div>

          {/* Gender Field */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
          >
            <Select
              label={t('patients.gender', 'Gender')}
              required
              options={genderOptions}
              error={errors.gender?.message}
              placeholder={t('patients.selectGender', 'Select Gender')}
              icon={<Users className="h-5 w-5" strokeWidth={2} />}
              name="gender"
              onChange={(value: string | number) => {
                setValue('gender', value as 'Male' | 'Female' | 'Other', { shouldValidate: true });
              }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Submit Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-4"
      >
        <Button
          type="submit"
          isLoading={isLoading}
          className="w-100"
        >
          {patient ? t('patients.updatePatient', 'Update Patient') : t('patients.createPatient', 'Create Patient')}
        </Button>
      </motion.div>
    </form>
  );
};

